import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { FriendlyTextField, FriendlyButton } from "../shared";

/**
 * DonorRegistration Component
 * Handles new donor registration form
 */
const DonorRegistration = ({
  t,
  email,
  initialData = {},
  onSubmit,
  isLoading = false,
  error = null,
  isCompact = false,
}) => {
  const [formData, setFormData] = useState({
    first_name: initialData.first_name || "",
    last_name: initialData.last_name || "",
    phone: initialData.phone || "",
    address: initialData.address || "",
    zip_code: initialData.zip_code || "",
  });

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    await onSubmit({
      first_name: formData.first_name,
      last_name: formData.last_name,
      phone: formData.phone,
      address: formData.address,
      zip_code: formData.zip_code,
    });
  };

  const isValid = formData.first_name?.trim() && formData.last_name?.trim();

  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      {/* Header */}
      {!isCompact && (
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "primary.100",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <PersonIcon sx={{ fontSize: 40, color: "primary.main" }} />
          </Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
          >
            {t("complete_registration", "Complete Your Registration")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("registration_required", "Please provide your information to continue")}
          </Typography>
        </Box>
      )}

      <Stack spacing={3}>
        {email && (
          <Alert severity="info" sx={{ py: 1 }}>
            {t("registering_as", "Registering as")}: <strong>{email}</strong>
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
            width: "100%",
          }}
        >
          <FriendlyTextField
            label={t("first_name", "First Name")}
            value={formData.first_name}
            onChange={handleChange("first_name")}
            required
            placeholder={t("first_name_placeholder", "John")}
          />
          <FriendlyTextField
            label={t("last_name", "Last Name")}
            value={formData.last_name}
            onChange={handleChange("last_name")}
            required
            placeholder={t("last_name_placeholder", "Doe")}
          />
        </Box>

        <FriendlyTextField
          label={t("phone", "Phone Number")}
          value={formData.phone}
          onChange={handleChange("phone")}
          placeholder={t("phone_placeholder", "(555) 123-4567")}
        />

        <FriendlyTextField
          label={t("address", "Address")}
          value={formData.address}
          onChange={handleChange("address")}
          placeholder={t("address_placeholder", "123 Main Street")}
        />

        <FriendlyTextField
          label={t("zip_code", "ZIP Code")}
          value={formData.zip_code}
          onChange={handleChange("zip_code")}
          placeholder={t("zip_code_placeholder", "12345")}
        />

        <FriendlyButton
          onClick={handleSubmit}
          disabled={!isValid || isLoading}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("register", "Register")
          )}
        </FriendlyButton>

        {error && (
          <Alert severity="error" sx={{ py: 1.5 }}>
            {error || t("registration_failed", "Registration failed. Please try again.")}
          </Alert>
        )}
      </Stack>
    </Box>
  );
};

export default DonorRegistration;
